import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getProductList } from '../services/productService';

const ProductSwitcherModal = ({ open, onClose, currentProductId, onSelect, onlyTour = true }) => {
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const currentItemRef = useRef(null);
  const scrolledForOpenRef = useRef(false);

  useEffect(() => { setMounted(true); return () => setMounted(false); }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!open) return;
      try {
        setLoading(true);
        const { companyNo, userNo } = getUserInfo() || {};
        const list = await getProductList(companyNo, userNo).catch(() => []);
        if (!active) return;
        let rows = Array.isArray(list) ? list : [];
        if (onlyTour) {
          rows = rows.filter(r => String(r?.상품종류코드) !== '001001');
        }
        setItems(rows);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [open, onlyTour]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(r =>
      String(r?.상품명 || '').toLowerCase().includes(q) ||
      String(r?.상품번호 || '').includes(q)
    );
  }, [items, query]);

  useEffect(() => {
    if (!open) {
      scrolledForOpenRef.current = false;
      return;
    }
    if (loading || !currentProductId || scrolledForOpenRef.current) return;

    const hasCurrent = filtered.some((r) => String(r?.상품번호) === String(currentProductId));
    if (!hasCurrent) return;

    const frame = requestAnimationFrame(() => {
      currentItemRef.current?.scrollIntoView({ block: 'start' });
      scrolledForOpenRef.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [open, loading, currentProductId, filtered]);

  if (!mounted) return null;
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-x-0 top-12 mx-auto max-w-2xl rounded-lg shadow-lg bg-white border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">다른 상품으로 이동</h3>
          <button className="text-gray-500 hover:text-gray-700 text-sm" onClick={onClose}>닫기</button>
        </div>
        <div className="p-3">
          <input
            type="text"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="상품명 또는 상품번호 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <div className="p-4 text-sm text-gray-600">불러오는 중...</div>
          ) : (
            <ul className="divide-y">
              {filtered.map(row => {
                const id = String(row?.상품번호);
                const isCurrent = String(currentProductId) === id;
                return (
                  <li
                    key={id}
                    ref={isCurrent ? currentItemRef : null}
                    className={`px-4 py-2 flex items-center justify-between gap-3 ${isCurrent ? 'bg-primary-50 ring-1 ring-inset ring-primary-100' : ''}`}
                  >
                    <div className="min-w-0">
                      <div className={`text-sm font-medium truncate ${isCurrent ? 'text-primary-800' : 'text-gray-900'}`}>{row?.상품명 || '-'}{isCurrent ? ' (현재)' : ''}</div>
                      <div className={`text-xs ${isCurrent ? 'text-primary-600' : 'text-gray-500'}`}>상품번호: {id}</div>
                    </div>
                    <div className="shrink-0">
                      <button
                        disabled={isCurrent}
                        className={`px-3 py-1.5 text-xs rounded border ${isCurrent ? 'bg-gray-100 text-gray-400' : 'bg-white hover:bg-gray-50 text-gray-700'}`}
                        onClick={() => { if (!isCurrent) { onSelect?.(id, row); } }}
                      >
                        이동
                      </button>
                    </div>
                  </li>
                );
              })}
              {filtered.length === 0 && !loading && (
                <li className="px-4 py-6 text-sm text-gray-500">검색 결과가 없습니다.</li>
              )}
            </ul>
          )}
        </div>
        <div className="p-3 border-t text-right">
          <button className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductSwitcherModal;
